import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SharedNav from '../../components/feature/SharedNav';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': `${SITE_URL}/group-health-insurance-kerrville-tx#breadcrumb`,
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Group Health Insurance Kerrville TX',
      item: `${SITE_URL}/group-health-insurance-kerrville-tx`,
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/group-health-insurance-kerrville-tx#webpage`,
  url: `${SITE_URL}/group-health-insurance-kerrville-tx`,
  name: 'Group Health Insurance Kerrville | Employee Benefits | 830-203-6201',
  description: 'Provide top-tier employee benefits. Get custom Group Health Insurance quotes in Kerrville, TX. Call 830-203-6201 to lower your premiums and attract top talent.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: { '@id': `${SITE_URL}/group-health-insurance-kerrville-tx#breadcrumb` },
};

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#localbusiness-group`,
  name: 'West Insurance',
  description: 'Health Insurance Agency serving Kerrville, TX and Kerr County. Specializing in Group Health Insurance, employee benefits, and small business health plans.',
  '@category': 'Health Insurance Agency',
  telephone: '+18302036201',
  url: SITE_URL,
  address: {
    '@type': 'PostalAddress',
    streetAddress: '243 La Reata Rd S',
    addressLocality: 'Kerrville',
    addressRegion: 'TX',
    postalCode: '78028',
    addressCountry: 'US',
  },
  areaServed: {
    '@type': 'City',
    name: 'Kerrville',
    containedInPlace: { '@type': 'State', name: 'Texas' },
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+18302036201',
    contactType: 'Sales and Quotes',
    areaServed: 'US',
    availableLanguage: 'English',
    contactOption: 'TollFree',
  },
};

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Group Health Insurance Kerrville TX',
  description: 'Affordable group health insurance for small businesses and employers in Kerrville, TX. Employee benefits plans for Kerr County companies. Call (830) 203-6201.',
  provider: {
    '@type': 'InsuranceAgency',
    name: 'West Health Insurance',
    telephone: '+18302036201',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '243 La Reata Rd S',
      addressLocality: 'Kerrville',
      addressRegion: 'TX',
      postalCode: '78028',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Kerrville', containedInPlace: { '@type': 'State', name: 'Texas' } },
  url: `${SITE_URL}/group-health-insurance-kerrville-tx`,
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How many employees do I need to offer group health insurance in Kerrville, TX?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'In Texas, businesses with as few as 2 full-time employees can qualify for small group health insurance. Some carriers allow sole proprietors to qualify under certain conditions. West Health Insurance helps Kerrville businesses of all sizes find the right group plan.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are there tax benefits for offering group health insurance to my Kerrville employees?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Employer contributions to group health insurance premiums are generally tax-deductible as a business expense. Additionally, small businesses may qualify for the Small Business Health Care Tax Credit if they have fewer than 25 full-time equivalent employees. Ask West Health Insurance about your eligibility.',
      },
    },
    {
      '@type': 'Question',
      name: 'What types of group health insurance plans are available for businesses in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Group health insurance options in Kerrville include PPO plans, HMO plans, HDHP (High-Deductible Health Plans) paired with HSA accounts, and level-funded plans for mid-size businesses. West Health Insurance compares all major Texas carriers to find the best fit for your business and budget.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I offer dental and vision insurance as part of a group plan in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely. Group dental and vision insurance can be added alongside your medical plan to create a comprehensive employee benefits package. These are highly valued by employees and relatively affordable for employers. West Health Insurance can bundle these for your Kerrville business.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I get a group health insurance quote for my Kerrville small business?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Getting a group health insurance quote is easy. Call West Health Insurance at (830) 203-6201 or visit our office at 243 La Reata Rd S, Kerrville, TX 78028. Our agents will gather basic information about your business and employees and present you with multiple plan options and costs.',
      },
    },
  ],
};

const benefits = [
  { icon: 'ri-user-smile-line', title: 'Attract Top Talent', desc: 'Competitive health benefits are a top factor for job seekers in the Texas Hill Country. Stand out from businesses that don\'t offer coverage.' },
  { icon: 'ri-heart-add-line', title: 'Reduce Employee Turnover', desc: 'Employees with health coverage feel more valued and are significantly less likely to leave for other employers.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Tax Advantages', desc: 'Employer contributions to group health premiums are tax-deductible. Small businesses may also qualify for the federal Small Business Health Care Tax Credit.' },
  { icon: 'ri-team-line', title: 'Healthier Workforce', desc: 'Employees with access to preventive care miss fewer workdays. Healthier teams mean higher productivity for your Kerrville business.' },
  { icon: 'ri-shield-flash-line', title: 'Employer Control', desc: "Choose your contribution level, plan type, and carrier. We'll design a group plan that works within your budget while maximizing employee value." },
  { icon: 'ri-calendar-check-line', title: 'Simplified Enrollment', desc: 'We manage the enrollment process for your employees, so you can focus on running your business. Ongoing support from our Kerrville office.' },
];

export default function GroupHealthInsurancePage() {
  useEffect(() => {
    document.title = 'Group Health Insurance Kerrville | Employee Benefits | 830-203-6201';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Provide top-tier employee benefits. Get custom Group Health Insurance quotes in Kerrville, TX. Call 830-203-6201 to lower your premiums and attract top talent.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/group-health-insurance-kerrville-tx`);

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.id = 'schema-group-service';
    s1.textContent = JSON.stringify(schemaData);
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    s2.type = 'application/ld+json';
    s2.id = 'schema-group-faq';
    s2.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(s2);

    const s3 = document.createElement('script');
    s3.type = 'application/ld+json';
    s3.id = 'schema-group-breadcrumb';
    s3.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(s3);

    const s4 = document.createElement('script');
    s4.type = 'application/ld+json';
    s4.id = 'schema-group-webpage';
    s4.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(s4);

    const s5 = document.createElement('script');
    s5.type = 'application/ld+json';
    s5.id = 'schema-group-localbusiness';
    s5.textContent = JSON.stringify(localBusinessSchema);
    document.head.appendChild(s5);

    return () => {
      document.title = 'West Health Insurance Kerrville TX | Private Health, Group & Medicare';
      s1.parentNode?.removeChild(s1);
      s2.parentNode?.removeChild(s2);
      s3.parentNode?.removeChild(s3);
      s4.parentNode?.removeChild(s4);
      s5.parentNode?.removeChild(s5);
    };
  }, []);

  const faqs = faqSchema.mainEntity;

  return (
    <div className="min-h-screen bg-white">
      <SharedNav />

      {/* Hero */}
      <section className="relative pt-20 min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/366efdb6e9a20e9144a6c3afae27bd7c.jpg"
            alt="Group Health Insurance for Small Businesses Kerrville TX"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/40 to-black/20"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B6]/80 backdrop-blur-sm rounded-full mb-6">
              <i className="ri-building-2-line text-white text-sm"></i>
              <span className="text-white text-sm font-medium">Small Business Health Benefits in Kerrville, TX</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Group Health Insurance<br />
              <span className="text-4xl md:text-5xl">for Kerrville Businesses</span>
            </h1>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Offer your Kerrville employees the health coverage they deserve. West Health Insurance helps small and mid-size businesses in Kerr County design affordable group health benefit plans that retain great employees and reduce taxes.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link to="/#contact" className="px-8 py-4 bg-[#0077B6] text-white font-semibold rounded-lg hover:bg-[#005F8C] transition-all whitespace-nowrap cursor-pointer">
                Get a Business Quote
              </Link>
              <a href="tel:830-203-6201" className="px-8 py-4 bg-white text-[#0077B6] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                Call 830-203-6201
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0077B6] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-6 text-center">
            {[
              { stat: '2+', label: 'Employees needed to qualify' },
              { stat: '100%', label: 'Employer premium deductible' },
              { stat: '50%', label: 'Avg employer contribution' },
              { stat: '24/7', label: 'Agent support available' },
            ].map((item) => (
              <div key={item.label}>
                <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{item.stat}</div>
                <div className="text-white/75 text-xs">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why Kerrville Businesses Offer Group Health Insurance
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14">
            A group health plan isn't just a benefit — it's a strategic investment in your Kerrville business's future.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b) => (
              <div key={b.title} className="p-8 bg-[#F0F8FF] rounded-2xl hover:bg-white transition-colors group">
                <div className="w-14 h-14 flex items-center justify-center bg-[#0077B6] rounded-xl mb-5 group-hover:scale-110 transition-transform">
                  <i className={`${b.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{b.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plan Types */}
      <section className="py-20 bg-[#F0F8FF]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=insurance%20agent%20presenting%20group%20health%20plan%20options%20to%20a%20small%20business%20owner%20at%20a%20desk%20in%20a%20modern%20office%20in%20Texas%20showing%20documents%20on%20a%20tablet%20clear%20bright%20professional%20setting%20blue%20and%20white%20color%20palette&width=700&height=550&seq=group-agent-002&orientation=landscape"
                alt="Group Health Insurance Options for Kerrville Small Businesses"
                className="w-full h-80 object-cover object-top rounded-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Group Plan Types Available in Kerrville
              </h2>
              <div className="space-y-4">
                {[
                  { name: 'PPO Group Plans', desc: 'Flexible network access — employees see any doctor without referrals. Most popular for Texas businesses.', tag: 'Most Flexible' },
                  { name: 'HMO Group Plans', desc: 'Lower premiums with a set network of providers. Great for cost-conscious Kerrville employers.', tag: 'Cost-Efficient' },
                  { name: 'HDHP + HSA Plans', desc: 'High-deductible plans paired with Health Savings Accounts. Employees build tax-free medical savings.', tag: 'HSA-Eligible' },
                  { name: 'Level-Funded Plans', desc: 'A hybrid approach for mid-size businesses. Pay a fixed monthly amount and potentially earn a refund if claims are low.', tag: 'Mid-Size Biz' },
                ].map((plan) => (
                  <div key={plan.name} className="bg-white rounded-xl p-5 flex gap-4 items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#0077B6] rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line text-white text-base"></i>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-bold text-gray-900 text-sm">{plan.name}</h4>
                        <span className="px-2 py-0.5 bg-[#F0F8FF] text-[#0077B6] text-xs font-semibold rounded-full">{plan.tag}</span>
                      </div>
                      <p className="text-gray-600 text-sm">{plan.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Group Health Insurance FAQs
          </h2>
          <p className="text-gray-600 text-center mb-14">Answers for Kerrville business owners considering group health benefits</p>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <details key={faq.name} className="group bg-[#F0F8FF] rounded-xl p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-gray-900 list-none cursor-pointer">
                  <span>{faq.name}</span>
                  <i className="ri-add-line text-[#0077B6] text-xl group-open:hidden"></i>
                  <i className="ri-subtract-line text-[#0077B6] text-xl hidden group-open:block"></i>
                </summary>
                <p className="mt-4 text-gray-600 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Get a Group Health Insurance Quote Today
          </h2>
          <p className="text-white/85 text-lg mb-8 max-w-xl mx-auto">
            Our Kerrville agents will compare top Texas carriers and present you with the best group health insurance options for your business — for free.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/#contact" className="px-8 py-4 bg-white text-[#0077B6] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
              Request a Free Business Quote
            </Link>
            <a href="tel:830-203-6201" className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
              Call (830) 203-6201
            </a>
          </div>
          <div className="mt-10 flex items-center justify-center gap-8 flex-wrap">
            <Link to="/private-health-insurance-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Private Health Insurance
            </Link>
            <Link to="/medicare-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Medicare Plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
